import React, { useState, useEffect, useCallback } from 'react';
import { evaluationAPI, employeeAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  ClipboardCheck, Plus, Pencil, Trash2, ChevronDown, ChevronUp, User,
  Download, FileSpreadsheet, FileText, Settings2, Save, X,
} from 'lucide-react';

const QUARTERS = [
  { value: '1', label: 'Q1 (Apr-Jun)' },
  { value: '2', label: 'Q2 (Jul-Sep)' },
  { value: '3', label: 'Q3 (Oct-Dec)' },
  { value: '4', label: 'Q4 (Jan-Mar)' },
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - i);

const fyLabel = (year) => `FY ${year}-${String(year + 1).slice(-2)}`;

const getScoreColor = (score) => {
  if (score >= 85) return 'text-green-600 bg-green-50';
  if (score >= 70) return 'text-blue-600 bg-blue-50';
  if (score >= 55) return 'text-amber-600 bg-amber-50';
  if (score >= 40) return 'text-orange-600 bg-orange-50';
  return 'text-red-600 bg-red-50';
};

const getBandColor = (band) => {
  const colors = {
    'Excellent': 'text-green-700 bg-green-100',
    'Very Good': 'text-blue-700 bg-blue-100',
    'Good': 'text-amber-700 bg-amber-100',
    'Needs Improvement': 'text-orange-700 bg-orange-100',
    'Poor': 'text-red-700 bg-red-100',
  };
  return colors[band] || 'text-slate-600 bg-slate-100';
};

const getBarColor = (val) => {
  if (val >= 8) return 'bg-green-500';
  if (val >= 6) return 'bg-blue-500';
  if (val >= 4) return 'bg-amber-500';
  return 'bg-red-500';
};

const SCORE_KEYS = Array.from({ length: 10 }, (_, i) => `criterion_${i + 1}`);

const Evaluations = () => {
  const [evaluations, setEvaluations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [criteria, setCriteria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingEval, setEditingEval] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('quarterly');

  // Criteria editing
  const [isEditingCriteria, setIsEditingCriteria] = useState(false);
  const [editCriteria, setEditCriteria] = useState([]);
  const [savingCriteria, setSavingCriteria] = useState(false);

  // Year Summary
  const [summaryYear, setSummaryYear] = useState(String(currentYear));
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Filters
  const [filterQuarter, setFilterQuarter] = useState('');
  const [filterYear, setFilterYear] = useState(String(currentYear));
  const [filterEmployee, setFilterEmployee] = useState('');

  // Form state
  const initScores = () => {
    const s = {};
    SCORE_KEYS.forEach(k => { s[k] = 0; });
    return s;
  };

  const [formData, setFormData] = useState({
    employee_id: '',
    quarter: '',
    year: String(currentYear),
    comments: '',
    key_strengths: '',
    improvement_area: '',
    ...initScores(),
  });

  const fetchCriteria = useCallback(async () => {
    try {
      const res = await evaluationAPI.getCriteria();
      setCriteria(res.data);
    } catch (e) {
      console.error('Failed to fetch criteria', e);
    }
  }, []);

  const fetchEvaluations = useCallback(async () => {
    try {
      const params = {};
      if (filterQuarter && filterQuarter !== 'all') params.quarter = filterQuarter;
      if (filterYear && filterYear !== 'all') params.year = filterYear;
      if (filterEmployee && filterEmployee !== 'all') params.employee_id = filterEmployee;
      const res = await evaluationAPI.getAll(params);
      setEvaluations(res.data);
    } catch (e) {
      console.error('Failed to fetch evaluations', e);
    } finally {
      setLoading(false);
    }
  }, [filterQuarter, filterYear, filterEmployee]);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await employeeAPI.getAll();
      setEmployees(res.data.filter(e => e.is_active));
    } catch (e) {
      console.error('Failed to fetch employees', e);
    }
  }, []);

  const fetchSummary = useCallback(async (year) => {
    setSummaryLoading(true);
    try {
      const res = await evaluationAPI.getSummary(year);
      setSummaryData(res.data);
    } catch (e) {
      console.error('Failed to fetch summary', e);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => { fetchCriteria(); fetchEmployees(); }, [fetchCriteria, fetchEmployees]);
  useEffect(() => { fetchEvaluations(); }, [fetchEvaluations]);
  useEffect(() => { if (activeTab === 'summary') fetchSummary(summaryYear); }, [activeTab, summaryYear, fetchSummary]);

  const criteriaMap = {};
  const weightMap = {};
  criteria.forEach(c => { criteriaMap[c.key] = c.label; weightMap[c.key] = c.weight || 10; });

  // Calculate weighted score for the form
  const calcWeightedScore = () => {
    let total = 0;
    SCORE_KEYS.forEach(k => {
      const raw = formData[k] || 0;
      const weight = weightMap[k] || 10;
      total += (raw / 10) * weight;
    });
    return Math.round(total * 10) / 10;
  };
  const weightedScore = calcWeightedScore();
  const getBand = (pct) => {
    if (pct >= 85) return 'Excellent';
    if (pct >= 70) return 'Very Good';
    if (pct >= 55) return 'Good';
    if (pct >= 40) return 'Needs Improvement';
    return 'Poor';
  };
  const formBand = getBand(weightedScore);

  const openCreate = () => {
    setEditingEval(null);
    setFormData({ employee_id: '', quarter: '', year: String(currentYear), comments: '', key_strengths: '', improvement_area: '', ...initScores() });
    setIsFormOpen(true);
  };

  const openEdit = (ev) => {
    setEditingEval(ev);
    const scores = {};
    SCORE_KEYS.forEach(k => { scores[k] = ev[k] || 0; });
    setFormData({
      employee_id: ev.employee_id,
      quarter: String(ev.quarter),
      year: String(ev.year),
      comments: ev.comments || '',
      key_strengths: ev.key_strengths || '',
      improvement_area: ev.improvement_area || '',
      ...scores,
    });
    setIsFormOpen(true);
  };

  const handleScoreChange = (key, value) => {
    const num = parseInt(value) || 0;
    const clamped = Math.min(10, Math.max(0, num));
    setFormData(p => ({ ...p, [key]: clamped }));
  };

  const totalScore = SCORE_KEYS.reduce((sum, k) => sum + (formData[k] || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.employee_id || !formData.quarter || !formData.year) return;
    setSaving(true);
    try {
      const payload = {
        employee_id: formData.employee_id,
        quarter: parseInt(formData.quarter),
        year: parseInt(formData.year),
        comments: formData.comments || null,
        key_strengths: formData.key_strengths || null,
        improvement_area: formData.improvement_area || null,
      };
      SCORE_KEYS.forEach(k => { payload[k] = formData[k]; });

      if (editingEval) {
        const updatePayload = { comments: formData.comments || null, key_strengths: formData.key_strengths || null, improvement_area: formData.improvement_area || null };
        SCORE_KEYS.forEach(k => { updatePayload[k] = formData[k]; });
        await evaluationAPI.update(editingEval.id, updatePayload);
      } else {
        await evaluationAPI.create(payload);
      }
      await fetchEvaluations();
      if (activeTab === 'summary') fetchSummary(summaryYear);
      setIsFormOpen(false);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save evaluation');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await evaluationAPI.delete(deleteTarget.id);
      await fetchEvaluations();
      if (activeTab === 'summary') fetchSummary(summaryYear);
    } catch (e) {
      console.error('Failed to delete', e);
    } finally {
      setDeleteTarget(null);
    }
  };

  // Criteria editing
  const startEditCriteria = () => {
    setEditCriteria(criteria.map(c => ({ ...c })));
    setIsEditingCriteria(true);
  };

  const saveCriteria = async () => {
    setSavingCriteria(true);
    try {
      await evaluationAPI.updateCriteria({ criteria: editCriteria.map(c => ({ key: c.key, label: c.label, weight: c.weight })) });
      await fetchCriteria();
      setIsEditingCriteria(false);
    } catch (e) {
      alert('Failed to save criteria');
    } finally {
      setSavingCriteria(false);
    }
  };

  // Export
  const handleExport = async (fmt) => {
    try {
      const year = activeTab === 'summary' ? summaryYear : filterYear;
      const yearNum = year && year !== 'all' ? year : currentYear;
      const res = fmt === 'excel'
        ? await evaluationAPI.exportExcel(yearNum)
        : await evaluationAPI.exportPdf(yearNum);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fmt === 'excel' ? `evaluation_FY${yearNum}.xlsx` : `evaluation_FY${yearNum}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed', e);
      alert('Export failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1D35A0]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="evaluations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-[#1D35A0]">Employee Evaluation</h2>
          <p className="text-slate-500 text-sm mt-1">Weighted scoring system — Indian FY (Apr-Mar) — Scores 0-10, auto-weighted %</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={startEditCriteria} data-testid="edit-criteria-btn">
            <Settings2 className="w-4 h-4 mr-1.5" /> Edit Headings
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport('excel')} data-testid="export-excel-btn">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport('pdf')} data-testid="export-pdf-btn">
            <FileText className="w-4 h-4 mr-1.5" /> PDF
          </Button>
          <Button size="sm" onClick={openCreate} className="bg-[#1D35A0] hover:bg-[#152A80] text-white" data-testid="new-evaluation-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Evaluation
          </Button>
        </div>
      </div>

      {/* Tabs: Quarterly View / Year Summary */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="quarterly" data-testid="tab-quarterly">Quarterly View</TabsTrigger>
          <TabsTrigger value="summary" data-testid="tab-summary">Year Summary</TabsTrigger>
        </TabsList>

        {/* === QUARTERLY VIEW === */}
        <TabsContent value="quarterly" className="space-y-5">
          {/* Filters */}
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Quarter</Label>
                <Select value={filterQuarter} onValueChange={setFilterQuarter}>
                  <SelectTrigger data-testid="filter-quarter"><SelectValue placeholder="All Quarters" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Quarters</SelectItem>
                    {QUARTERS.map(q => <SelectItem key={q.value} value={q.value}>{q.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Financial Year</Label>
                <Select value={filterYear} onValueChange={setFilterYear}>
                  <SelectTrigger data-testid="filter-year"><SelectValue placeholder="All Years" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Years</SelectItem>
                    {YEARS.map(y => <SelectItem key={y} value={String(y)}>{fyLabel(y)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Employee</Label>
                <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                  <SelectTrigger data-testid="filter-employee"><SelectValue placeholder="All Employees" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Employees</SelectItem>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Evaluation List */}
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
            {evaluations.length === 0 ? (
              <div className="text-center py-16" data-testid="no-evaluations">
                <ClipboardCheck className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 font-medium">No evaluations found</p>
                <p className="text-slate-400 text-sm mt-1">Create a new quarterly evaluation to get started</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-200">
                {evaluations.map(ev => (
                  <div key={ev.id} className="transition-colors" data-testid={`evaluation-${ev.id}`}>
                    <div
                      className="px-3 sm:px-5 py-3 sm:py-4 cursor-pointer hover:bg-slate-50"
                      onClick={() => setExpandedId(expandedId === ev.id ? null : ev.id)}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 sm:w-5 sm:h-5 text-slate-500" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 truncate text-sm">{ev.employee_name}</p>
                            <p className="text-[10px] sm:text-xs text-slate-500 truncate">{ev.designation} · {ev.department}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
                          <span className="text-[10px] sm:text-xs font-medium text-slate-500 bg-slate-100 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full whitespace-nowrap">
                            Q{ev.quarter} {fyLabel(ev.year)}
                          </span>
                          <span className={`text-base sm:text-lg font-bold px-2 sm:px-3 py-0.5 sm:py-1 rounded-lg ${getScoreColor(ev.weighted_score || ev.total_points)}`} data-testid={`score-${ev.id}`}>
                            {ev.weighted_score || ev.total_points}<span className="text-[10px] sm:text-xs font-normal">%</span>
                          </span>
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full hidden sm:inline-flex ${getBandColor(ev.performance_band || '')}`}>
                            {ev.performance_band || ''}
                          </span>
                          {expandedId === ev.id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                        </div>
                      </div>
                    </div>
                    {expandedId === ev.id && (
                      <div className="px-3 sm:px-5 pb-4 sm:pb-5 border-t border-slate-100 bg-slate-50/50">
                        <div className="flex items-center gap-1 justify-end pt-2 pb-1">
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); openEdit(ev); }} data-testid={`edit-eval-${ev.id}`}>
                            <Pencil className="w-3.5 h-3.5 text-slate-500" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500" onClick={(e) => { e.stopPropagation(); setDeleteTarget(ev); }} data-testid={`delete-eval-${ev.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3">
                          {criteria.map(c => (
                            <div key={c.key} className="bg-white rounded-lg p-2 sm:p-3 border border-slate-200">
                              <p className="text-[10px] sm:text-xs text-slate-500 mb-0.5 sm:mb-1 leading-tight">{c.label}</p>
                              <p className="text-[9px] sm:text-[10px] text-slate-400">Wt: {c.weight || 10}%</p>
                              <div className="flex items-center gap-1 sm:gap-2 mt-1">
                                <span className="text-sm sm:text-lg font-bold text-[#1D35A0]">{ev[c.key] || 0}</span>
                                <span className="text-[10px] text-slate-400">/10</span>
                                <span className="text-[10px] text-slate-400 ml-auto">= {(((ev[c.key] || 0) / 10) * (c.weight || 10)).toFixed(1)}%</span>
                              </div>
                              <div className="w-full bg-slate-200 rounded-full h-1 sm:h-1.5 mt-1">
                                <div className={`h-1 sm:h-1.5 rounded-full ${getBarColor(ev[c.key] || 0)}`} style={{ width: `${(ev[c.key] || 0) * 10}%` }} />
                              </div>
                            </div>
                          ))}
                        </div>
                        {(ev.key_strengths || ev.improvement_area || ev.comments) && (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                            {ev.key_strengths && (
                              <div className="bg-white rounded-lg p-3 border border-green-200">
                                <p className="text-xs text-green-600 font-medium mb-1">Key Strengths</p>
                                <p className="text-sm text-slate-700">{ev.key_strengths}</p>
                              </div>
                            )}
                            {ev.improvement_area && (
                              <div className="bg-white rounded-lg p-3 border border-amber-200">
                                <p className="text-xs text-amber-600 font-medium mb-1">Improvement Area</p>
                                <p className="text-sm text-slate-700">{ev.improvement_area}</p>
                              </div>
                            )}
                            {ev.comments && (
                              <div className="bg-white rounded-lg p-3 border border-slate-200">
                                <p className="text-xs text-slate-500 mb-1">Comments</p>
                                <p className="text-sm text-slate-700">{ev.comments}</p>
                              </div>
                            )}
                          </div>
                        )}
                        <p className="text-xs text-slate-400 mt-3">
                          Evaluated by {ev.evaluated_by_name} on {new Date(ev.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* === YEAR SUMMARY VIEW === */}
        <TabsContent value="summary" className="space-y-5">
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Label className="text-sm font-medium text-slate-700">Financial Year</Label>
                <Select value={summaryYear} onValueChange={setSummaryYear}>
                  <SelectTrigger className="w-48" data-testid="summary-year"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {YEARS.map(y => <SelectItem key={y} value={String(y)}>{fyLabel(y)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {summaryLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1D35A0]"></div>
            </div>
          ) : summaryData && summaryData.employees.length > 0 ? (
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid="summary-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#1D35A0] text-white">
                      <th className="text-left px-4 py-3 font-medium sticky left-0 bg-[#1D35A0] z-10">Employee</th>
                      {QUARTERS.map(q => (
                        <th key={q.value} className="text-center px-3 py-3 font-medium">{q.label}</th>
                      ))}
                      <th className="text-center px-3 py-3 font-medium bg-[#0F1D32]">Year Avg</th>
                      <th className="text-center px-3 py-3 font-medium bg-[#0F1D32]">Rating</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {summaryData.employees.map((emp, idx) => (
                      <tr key={emp.employee_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} data-testid={`summary-row-${emp.employee_id}`}>
                        <td className="px-4 py-3 sticky left-0 bg-inherit z-10">
                          <p className="font-medium text-slate-900 text-sm">{emp.employee_name}</p>
                          <p className="text-xs text-slate-400">{emp.designation}</p>
                        </td>
                        {[1, 2, 3, 4].map(q => {
                          const qdata = emp.quarters[q];
                          return (
                            <td key={q} className="text-center px-3 py-3">
                              {qdata ? (
                                <span className={`inline-flex items-center justify-center font-bold text-sm px-2.5 py-1 rounded-lg ${getScoreColor(qdata.weighted_score || qdata.total_points)}`}>
                                  {qdata.weighted_score || qdata.total_points}%
                                </span>
                              ) : (
                                <span className="text-slate-300">-</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="text-center px-3 py-3">
                          <span className={`inline-flex items-center justify-center font-bold text-sm px-2.5 py-1 rounded-lg ${getScoreColor(emp.yearly_weighted_avg || emp.yearly_average)}`}>
                            {emp.yearly_weighted_avg || emp.yearly_average}%
                          </span>
                        </td>
                        <td className="text-center px-3 py-3">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getBandColor(emp.yearly_band || '')}`}>
                            {emp.yearly_band || ''}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Criteria Averages Breakdown */}
              <div className="border-t border-slate-200 p-5">
                <h4 className="text-sm font-semibold text-[#1D35A0] mb-3">Criteria-wise Yearly Averages</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="text-left px-3 py-2 font-medium text-slate-600">Employee</th>
                        {criteria.map(c => (
                          <th key={c.key} className="text-center px-2 py-2 font-medium text-slate-600 text-xs">{c.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {summaryData.employees.map(emp => (
                        <tr key={emp.employee_id}>
                          <td className="px-3 py-2 font-medium text-slate-800 text-sm">{emp.employee_name}</td>
                          {criteria.map(c => (
                            <td key={c.key} className="text-center px-2 py-2">
                              <div className="flex flex-col items-center gap-0.5">
                                <span className="text-sm font-semibold text-slate-700">{emp.criterion_averages?.[c.key] || 0}</span>
                                <div className="w-full max-w-[40px] bg-slate-200 rounded-full h-1">
                                  <div className={`h-1 rounded-full ${getBarColor(emp.criterion_averages?.[c.key] || 0)}`} style={{ width: `${(emp.criterion_averages?.[c.key] || 0) * 10}%` }} />
                                </div>
                              </div>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-12 text-center" data-testid="no-summary">
              <ClipboardCheck className="w-8 h-8 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No evaluations for {fyLabel(Number(summaryYear))}</p>
              <p className="text-slate-400 text-sm mt-1">Create quarterly evaluations to see the year summary</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Evaluation Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEval ? 'Edit Evaluation' : 'New Quarterly Evaluation'}</DialogTitle>
            <DialogDescription>Indian FY ({QUARTERS.map(q => q.label).join(', ')}) — Weighted scoring</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Employee *</Label>
                <Select value={formData.employee_id} onValueChange={(v) => setFormData(p => ({ ...p, employee_id: v }))} disabled={!!editingEval}>
                  <SelectTrigger data-testid="select-eval-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Quarter *</Label>
                <Select value={formData.quarter} onValueChange={(v) => setFormData(p => ({ ...p, quarter: v }))} disabled={!!editingEval}>
                  <SelectTrigger data-testid="select-eval-quarter"><SelectValue placeholder="Select quarter" /></SelectTrigger>
                  <SelectContent>
                    {QUARTERS.map(q => <SelectItem key={q.value} value={q.value}>{q.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Financial Year *</Label>
                <Select value={formData.year} onValueChange={(v) => setFormData(p => ({ ...p, year: v }))} disabled={!!editingEval}>
                  <SelectTrigger data-testid="select-eval-year"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {YEARS.map(y => <SelectItem key={y} value={String(y)}>{fyLabel(y)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-[#1D35A0]">Scoring (each out of 10, weighted)</p>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-bold px-3 py-1 rounded-lg ${getScoreColor(weightedScore)}`}>
                    {weightedScore}%
                  </span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getBandColor(formBand)}`}>
                    {formBand}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {criteria.map(c => (
                  <div key={c.key} className="flex items-center gap-3 bg-white rounded-lg p-2.5 border border-slate-200">
                    <div className="flex-1 min-w-0">
                      <Label className="text-xs text-slate-600 leading-tight block">{c.label}</Label>
                      <span className="text-[10px] text-slate-400">Weight: {c.weight || 10}%</span>
                    </div>
                    <Input
                      type="number"
                      min={0}
                      max={10}
                      value={formData[c.key] || 0}
                      onChange={(e) => handleScoreChange(c.key, e.target.value)}
                      className="w-16 text-center font-bold text-lg h-9"
                      data-testid={`input-${c.key}`}
                    />
                    <span className="text-xs text-slate-400 w-5">/10</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Key Strengths</Label>
                <Textarea
                  value={formData.key_strengths}
                  onChange={(e) => setFormData(p => ({ ...p, key_strengths: e.target.value }))}
                  placeholder="Notable strengths and accomplishments..."
                  rows={2}
                  data-testid="input-key-strengths"
                />
              </div>
              <div className="space-y-2">
                <Label>Improvement Area</Label>
                <Textarea
                  value={formData.improvement_area}
                  onChange={(e) => setFormData(p => ({ ...p, improvement_area: e.target.value }))}
                  placeholder="Areas needing development..."
                  rows={2}
                  data-testid="input-improvement-area"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Reviewer Comments</Label>
              <Textarea
                value={formData.comments}
                onChange={(e) => setFormData(p => ({ ...p, comments: e.target.value }))}
                placeholder="Overall remarks, goals, next steps..."
                rows={2}
                data-testid="input-eval-comments"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>Cancel</Button>
              <Button
                type="submit"
                className="bg-[#1D35A0] hover:bg-[#152A80] text-white"
                disabled={saving || !formData.employee_id || !formData.quarter || !formData.year}
                data-testid="submit-evaluation-btn"
              >
                {saving ? 'Saving...' : editingEval ? 'Update Evaluation' : 'Submit Evaluation'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Criteria Headings Dialog */}
      <Dialog open={isEditingCriteria} onOpenChange={setIsEditingCriteria}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Evaluation Criteria</DialogTitle>
            <DialogDescription>Customize labels and weights (must total 100%)</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {editCriteria.map((c, idx) => (
              <div key={c.key} className="flex items-center gap-3">
                <span className="text-xs text-slate-400 w-6 text-right">{idx + 1}.</span>
                <Input
                  value={c.label}
                  onChange={(e) => {
                    const updated = [...editCriteria];
                    updated[idx] = { ...updated[idx], label: e.target.value };
                    setEditCriteria(updated);
                  }}
                  className="flex-1"
                  data-testid={`edit-criterion-${idx + 1}`}
                />
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={c.weight || 10}
                  onChange={(e) => {
                    const updated = [...editCriteria];
                    updated[idx] = { ...updated[idx], weight: parseInt(e.target.value) || 0 };
                    setEditCriteria(updated);
                  }}
                  className="w-20 text-center"
                  data-testid={`edit-weight-${idx + 1}`}
                />
                <span className="text-xs text-slate-500">%</span>
              </div>
            ))}
            <div className="flex justify-end">
              <span className={`text-xs font-medium px-2 py-1 rounded ${editCriteria.reduce((s, c) => s + (c.weight || 10), 0) === 100 ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'}`}>
                Total: {editCriteria.reduce((s, c) => s + (c.weight || 10), 0)}%
              </span>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setIsEditingCriteria(false)}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button onClick={saveCriteria} className="bg-[#1D35A0] hover:bg-[#152A80] text-white" disabled={savingCriteria} data-testid="save-criteria-btn">
              <Save className="w-4 h-4 mr-1" /> {savingCriteria ? 'Saving...' : 'Save Headings'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Evaluation</AlertDialogTitle>
            <AlertDialogDescription>
              Delete Q{deleteTarget?.quarter} {deleteTarget?.year && fyLabel(deleteTarget.year)} evaluation for {deleteTarget?.employee_name}? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-eval">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Evaluations;
